import { DesignData } from '../types';

import getPageCountStep from './getPageCountStep';

// The min/max/increment window a multipage product's page count must stay
// within, merged from the design's page_count_limit and (when sold online) the
// ecommerce variant grid — see usePageCountLimit.
export interface PageCountLimit {
  min: number;
  max: number;
  increment?: number;
}

// A single "add/remove pages" click moves page_count by a whole grid step (see
// getPageCountStep), so gating must look that full step ahead: a click must
// never land outside [min, max] / off the valid grid. The helpers below are the
// single source of truth for when the add / duplicate / remove controls are
// enabled — shared by the top-menu buttons and the per-spread thumbnail menu.

export function canAddPagesForSpread(
  designData: DesignData | undefined,
  canAddAfter: boolean,
  pageCountLimit: PageCountLimit | undefined,
): boolean {
  if (!pageCountLimit || !canAddAfter) {
    return false;
  }
  const step = getPageCountStep(designData, pageCountLimit.increment);
  const pageCount = designData?.page_count ?? 0;
  return pageCount + step <= pageCountLimit.max;
}

export function canRemovePagesForSpread(
  designData: DesignData | undefined,
  canRemove: boolean,
  pageCountLimit: PageCountLimit | undefined,
): boolean {
  if (!pageCountLimit || !canRemove) {
    return false;
  }
  const step = getPageCountStep(designData, pageCountLimit.increment);
  const pageCount = designData?.page_count ?? 0;
  return pageCount - step >= pageCountLimit.min;
}

// Whether removing one grid step would drop below the minimum page count,
// regardless of the spread's own canRemove flag. Drives the "can't delete
// pages" tooltip.
export function isAtMinPageCount(
  designData: DesignData | undefined,
  pageCountLimit: PageCountLimit | undefined,
): boolean {
  if (!pageCountLimit) {
    return false;
  }
  const step = getPageCountStep(designData, pageCountLimit.increment);
  const pageCount = designData?.page_count ?? 0;
  return pageCount - step < pageCountLimit.min;
}
